/**
 * EL ESTADO DE SEGUIMIENTO DE UNA VENTA — un solo lugar, tres consumidores.
 *
 * POR QUÉ ES UNA FUNCIÓN PURA Y NO LÓGICA EN CADA COMPONENTE (12-ago-2026)
 * ───────────────────────────────────────────────────────────────────────
 * El badge de la lista, el banner del detalle y el filtro del inbox necesitan
 * los tres la misma respuesta: ¿esta venta está activa, vencida, o no hay que
 * mostrarla? Si cada uno la calculara por su cuenta, tendríamos tres
 * implementaciones del mismo conocimiento que empiezan iguales y divergen — que
 * es exactamente el patrón que ya está anotado con los dos lectores del ancla
 * `pux_*` (`hasAttribution` en el agregador de métricas y `leerAtribucionPux`).
 *
 * POR QUÉ CALCULADO Y NO PERSISTIDO
 * ─────────────────────────────────
 * La alternativa era escribir "vencida" a las 48 h con un job. Se descartó por
 * dos razones medidas:
 *
 *   · el inbox **ya filtra en el cliente** — `Chats.js` trae los chats con
 *     `limit(100)` y sin un solo filtro de Firestore ("cargar todos y filtrar en
 *     cliente", dice su propio comentario). Así que el argumento en contra de
 *     calcular —que una consulta no puede filtrar por un valor derivado— no
 *     aplica: no hay consulta que romper;
 *   · un estado persistido puede DISCREPAR del reloj: el campo diría "activa" y
 *     la fecha diría otra cosa. Calcularlo lo hace imposible por construcción, y
 *     esa clase de defecto —dos representaciones del mismo hecho— ya se pagó
 *     varias veces en este repo.
 *
 * El costo aceptado: un job futuro no puede consultar "dame las vencidas"
 * directamente. Hoy ese job no existe ni está pedido.
 */

/** 48 horas, la caducidad decidida para el seguimiento. */
export const VENTANA_SEGUIMIENTO_MS = 48 * 60 * 60 * 1000;

/**
 * Devuelve la última venta registrada en un chat, o null.
 *
 * Lee del historial y cae a `ventaConcretada` para los chats escritos antes de
 * que el historial existiera. No es compatibilidad hacia atrás con datos reales
 * —no hay clientes— sino tolerancia a un documento a medio migrar.
 */
export function ultimaVenta(chat) {
  const delHistorial = Array.isArray(chat?.ventasHistorial) ? chat.ventasHistorial[0] : null;
  return delHistorial || chat?.ventaConcretada || null;
}

/**
 * El estado de seguimiento de un chat.
 *
 * @returns {'ninguna'|'activa'|'vencida'}
 *   · `ninguna` — no hubo venta, o el usuario ya la descartó
 *   · `activa`  — venta de menos de 48 h
 *   · `vencida` — más de 48 h, **pero SIGUE VISIBLE**: sólo la quita el usuario
 */
export function estadoSeguimientoVenta(chat, ahora = Date.now()) {
  const venta = ultimaVenta(chat);
  const ocurrida = venta?.occurredAt ? Date.parse(venta.occurredAt) : NaN;
  if (!Number.isFinite(ocurrida)) return 'ninguna';

  // El descarte sólo silencia las ventas ANTERIORES a él. Si el cliente vuelve a
  // comprar después de que el usuario limpió la marca, la marca vuelve — que es
  // lo que se espera de un cliente recurrente.
  const descartado = chat?.seguimientoVenta?.descartadoEn
    ? Date.parse(chat.seguimientoVenta.descartadoEn)
    : NaN;
  if (Number.isFinite(descartado) && descartado >= ocurrida) return 'ninguna';

  return (ahora - ocurrida) > VENTANA_SEGUIMIENTO_MS ? 'vencida' : 'activa';
}

/** ¿Hay que mostrar la marca? Activa y vencida se muestran las dos. */
export function tieneSeguimientoVisible(chat, ahora = Date.now()) {
  return estadoSeguimientoVenta(chat, ahora) !== 'ninguna';
}

/** Cuántas compras hizo este cliente en total (no sólo las del historial). */
export function totalDeVentas(chat) {
  if (Number.isFinite(chat?.ventasTotal)) return chat.ventasTotal;
  if (Array.isArray(chat?.ventasHistorial)) return chat.ventasHistorial.length;
  return chat?.ventaConcretada ? 1 : 0;
}
